import React, { useState } from "react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { useScene } from "./scene/SceneController";
import ModalPopup from "../../../components/popups/DefaultPopUp";

const HeroSection = () => {
    const brandName = process.env.REACT_APP_BRAND_NAME;
    const [showDemoModal, setShowDemoModal] = useState(false);

    const { updateScene } = useScene();

    useEffect(() => {
        updateScene({
            section: "hero",
            chaos: 0.25,
            organization: 0,
            coreIntensity: 1,
            cameraDistance: 8,
        });
    }, [updateScene]);

    return (
        <section
            id="hero"
            className="
                tw-relative
                tw-min-h-screen
                tw-flex
                tw-items-center
                tw-justify-center
                tw-overflow-hidden
                tw-px-6
            "
        >
            <div
                className="tw-absolute tw-inset-0 tw-opacity-10"
                style={{
                    backgroundImage: `
                        linear-gradient(rgba(255,255,255,.08) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255,255,255,.08) 1px, transparent 1px)
                    `,
                    backgroundSize: "60px 60px",
                }}
            />

            <div
                className="
                tw-absolute
                tw-inset-0
                tw-bg-gradient-to-b
                tw-from-[#050816]
                tw-via-[#07152f]/80
                tw-to-transparent
                "
            />

            <div
                className="
                    tw-relative
                    tw-z-10
                    tw-max-w-5xl
                    tw-text-center
                    tw-pt-20
                "
            >
                <motion.div
                    initial={{
                        opacity: 0,
                        y: 20,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                    }}
                    transition={{
                        duration: 0.8,
                    }}
                    className="
                        tw-inline-flex
                        tw-items-center
                        tw-gap-2
                        tw-px-4
                        tw-py-2
                        tw-rounded-full
                        tw-bg-white/10
                        tw-backdrop-blur-xl
                        tw-border
                        tw-border-white/10
                        tw-text-blue-300
                        tw-text-sm
                        tw-mb-8
                    "
                >
                    <span
                        className="
                        tw-w-2
                        tw-h-2
                        tw-rounded-full
                        tw-bg-cyan-400
                        tw-animate-pulse
                        "
                    />
                    The operating system for modern CA firms
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 40, }}
                    animate={{ opacity: 1, y: 0, }}
                    transition={{ duration: 1, delay: 0.2, }}
                    className="
                        tw-text-5xl
                        tw-md:text-7xl
                        tw-font-bold
                        tw-tracking-tight
                        tw-leading-[1.1]
                        tw-text-white
                    "
                >
                    Your accountants were
                    <br />
                    built to{" "}
                    <span
                        className="
                            tw-bg-gradient-to-r
                            tw-from-blue-400
                            tw-via-cyan-400
                            tw-to-purple-400
                            tw-bg-clip-text
                            tw-text-transparent
                        "
                    >
                        think.
                    </span>
                    <br />
                    Not just enter data.
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                    className="
                        tw-mt-8
                        tw-max-w-3xl
                        tw-mx-auto
                        tw-text-lg
                        tw-md:text-xl
                        tw-text-gray-300
                        tw-leading-relaxed
                    "
                >
                    {brandName} organizes documents, automates repetitive workflows and gives accounting teams real-time visibility — so professionals spend less time managing tasks and more time delivering expertise.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20, }}
                    animate={{ opacity: 1, y: 0, }}
                    transition={{ delay: 0.8, duration: 0.8, }}
                    className="
                        tw-mt-10
                        tw-flex
                        tw-flex-col
                        md:tw-flex-row
                        tw-items-center
                        tw-justify-center
                        tw-gap-5
                    "
                >
                    <motion.button
                        whileHover={{ scale: 1.05, boxShadow: "0 0 35px rgba(59,130,246,.6)" }}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => setShowDemoModal(true)}
                        className="
                            tw-px-8
                            tw-py-4
                            tw-rounded-xl
                            tw-bg-gradient-to-r
                            tw-from-blue-600
                            tw-to-cyan-500
                            tw-font-semibold
                            tw-text-white
                            tw-shadow-xl
                        "
                    >
                        Book a Demo
                    </motion.button>

                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        className="
                            tw-px-8
                            tw-py-4
                            tw-rounded-xl
                            tw-bg-white/10
                            tw-backdrop-blur-xl
                            tw-border
                            tw-border-white/20
                            tw-text-white
                        "
                    >
                        See How It Works
                    </motion.button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: [0, -10, 0] }}
                    transition={{
                        opacity: { duration: 1 },
                        y: {
                            duration: 5,
                            repeat: Infinity,
                            ease: "easeInOut",
                        },
                    }}
                    className="
                        tw-mt-12
                        tw-text-sm
                        tw-text-gray-400
                    "
                >
                    Automation handles the repetitive work. Your team keeps the judgement.
                </motion.div>
            </div>

            <div
                className="
                    tw-absolute
                    tw-bottom-0
                    tw-left-0
                    tw-right-0
                    tw-h-40
                    tw-bg-gradient-to-t
                    tw-from-[#050816]
                    tw-to-transparent
                "
            />

            <ModalPopup
                isOpen  = { showDemoModal }
                onClose = { () => setShowDemoModal(false) }
                title   = { `Book a ${brandName} Demo` }
                subtext = { `Ready to boost your firm's output? Call us at +91 97709 69714 to schedule your live ${brandName} demo and see how automation transforms your workflow.`}
                cancelText = "Maybe Later"
                actionText = "Connect Instantly"
                onAction   = { () => {
                    window.location.href = "tel:+1234567890"; // Opens the dialler
                    setShowDemoModal(false);                  // Closes the popup 
                }}
            />
        </section>
    );
};

export default HeroSection;